/**
 * What the account holds — `GET /me/entitlements` — under one query key.
 *
 * Its own small module (2026-10-08) because the app shell's menu asks for it
 * too (the tier beside "Plans"), and the shell is in every host's first
 * download: importing it from `visitor/RaveOptions` would have pulled the
 * paywall's Stripe loader in with it.
 */
import { useQuery } from '@tanstack/react-query';
import type { EntitlementSummary } from '@myrave/shared';
import { api } from './api';

export const ENTITLEMENTS_KEY = ['me', 'entitlements'] as const;

export function useEntitlements(enabled = true) {
  return useQuery({
    queryKey: ENTITLEMENTS_KEY,
    queryFn: () => api.get<EntitlementSummary>('/me/entitlements'),
    enabled,
  });
}

/** What can open a paid rave without paying again: this year's tier raves and pack credits. */
export function spendable(e: EntitlementSummary | undefined): { tierRaves: number; credits: number } {
  const tierRaves = e?.tier && typeof e.tier.ravesLeft === 'number' && e.tier.ravesLeft > 0 ? e.tier.ravesLeft : 0;
  const credits = typeof e?.raveCredits === 'number' && e.raveCredits > 0 ? e.raveCredits : 0;
  return { tierRaves, credits };
}

type Tr = (english: string, vars?: Record<string, string | number>) => string;

/**
 * A platform administrator's plan, "Master Raver" (the user, 2026-10-10):
 * everything, nothing to buy, nothing to change. `/me/entitlements` says so
 * with `master: true`; `tier` stays null for one.
 */
export function isMaster(e: EntitlementSummary | undefined | null): boolean {
  return e?.master === true;
}

/** The plan's full name: "Master Raver", "MyRave PLUS" or "Free Raver". */
export function planName(e: EntitlementSummary | undefined | null, tr: Tr): string {
  if (isMaster(e)) return tr('Master Raver');
  return e?.tier ? tr('MyRave {tier}', { tier: e.tier.tier }) : tr('Free Raver');
}

/** The chip beside "Plans" in a menu: "Master Raver", the tier ("PLUS"), or nothing. */
export function planChip(e: EntitlementSummary | undefined | null, tr: Tr): string | null {
  if (isMaster(e)) return tr('Master Raver');
  return e?.tier?.tier ?? null;
}
